import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Alert, Box, CircularProgress, IconButton, Link, Tab, Table, TableBody, TableHead, Tabs, ToggleButton,
    ToggleButtonGroup, Toolbar, Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { FraudFlagListItem, FraudSort, FraudStatus, getFraudFlags } from "../services/fraudService";
import { useCursorList } from "../hooks/useCursorList";
import { useCan } from "../auth/Can";
import { getApiErrorMessage } from "../lib/toast";
import LoadMoreBar from "../components/common/LoadMoreBar";
import {
    StyledPaper, StyledTableCell, StyledTableContainer, StyledTableRow,
} from "../assets/styles/tableStyle";
import { FraudPctChip, FraudSignalChips } from "./fraud/FraudChips";
import FraudExplanation from "./fraud/FraudExplanation";
import FraudPeople from "./fraud/FraudPeople";
import FraudReviewActions from "./fraud/FraudReviewActions";
import {
    bookingSourceLabel, formatDateTime, formatFare, fraudReviewerName, partySplit, responsibleLabel, vehicleTypeLabel,
} from "../utils/fraud";

type View = FraudStatus | "PEOPLE";

const TABS: { value: View; label: string }[] = [
    { value: "OPEN", label: "Open" },
    { value: "CONFIRMED", label: "Confirmed" },
    { value: "CLEARED", label: "Cleared" },
    { value: "PEOPLE", label: "People at risk" },
];

const PAGE_SIZE = 20;

const FraudReview: React.FC = () => {
    const { can } = useCan();
    const canReview = can("fraud:review");
    const [view, setView] = useState<View>("OPEN");
    const [sort, setSort] = useState<FraudSort>("pct");
    const [expanded, setExpanded] = useState<number | null>(null);
    const showFlags = view !== "PEOPLE";

    const flags = useCursorList<FraudFlagListItem, number | string>({
        key: ["fraud-flags", view, sort],
        pageSize: PAGE_SIZE,
        enabled: showFlags,
        // Errors (including a 403) render inline below instead of a global toast.
        meta: { suppressErrorToast: true },
        fetchPage: async ({ cursor, limit }) => {
            const res = await getFraudFlags({
                status: view as FraudStatus, cursor, limit, sort: sort === "pct" ? "pct" : undefined,
            });
            const { items, nextCursor } = res.data;
            return { items, nextCursor, hasMore: nextCursor != null };
        },
    });

    const loading = flags.isLoading || (flags.isFetching && !flags.isLoadingMore);
    const columns = [
        "", "Ride", "Likelihood", "Responsibility", "Completed", "Booked via", "Vehicle", "Fare", "Signals", "Flagged", "Review",
        ...(canReview ? ["Actions"] : []),
    ];
    const tabLabel = TABS.find((t) => t.value === view)!.label.toLowerCase();

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: "0 auto", my: 2 }}>
            <Toolbar sx={{ flexDirection: "column", alignItems: "flex-start", gap: 0.5, py: 1.5 }}>
                <Typography variant="h6">Fraud review</Typography>
                <Typography variant="body2" color="text.secondary">
                    Rides flagged as potential fraud by the nightly review (03:30 IST). A flag is never used in billing.
                </Typography>
            </Toolbar>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, px: 2 }}>
                <Tabs value={view} onChange={(_e, v: View) => setView(v)} variant="scrollable" allowScrollButtonsMobile>
                    {TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
                </Tabs>
                {showFlags && (
                    <ToggleButtonGroup
                        size="small" exclusive value={sort}
                        onChange={(_e, v: FraudSort | null) => v && setSort(v)}
                        aria-label="Sort flags"
                    >
                        <ToggleButton value="pct">Most likely first</ToggleButton>
                        <ToggleButton value="newest">Newest first</ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Box>

            {!showFlags ? <FraudPeople /> : (
                <>
                    {flags.isError && (
                        <Alert severity="error" sx={{ mx: 2, my: 1 }}>
                            {getApiErrorMessage(flags.error, "Couldn't load flagged rides")}
                        </Alert>
                    )}

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StyledTableContainer>
                            <Table stickyHeader sx={{ minWidth: 1200 }}>
                                <TableHead>
                                    <StyledTableRow>
                                        {columns.map((label, i) => <StyledTableCell key={i}>{label}</StyledTableCell>)}
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {flags.rows.map((flag) => {
                                        const open = expanded === flag.id;
                                        return (
                                            <React.Fragment key={flag.id}>
                                                <StyledTableRow hover data-testid={`fraud-row-${flag.rideId}`}>
                                                    <StyledTableCell>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`${open ? "Hide" : "Show"} explanation for ride #${flag.rideId}`}
                                                            onClick={() => setExpanded(open ? null : flag.id)}
                                                        >
                                                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                        </IconButton>
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <Link component={RouterLink} to={`/ride/${flag.rideId}`} sx={{ fontWeight: 600 }}>
                                                            #{flag.rideId}
                                                        </Link>
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        <FraudPctChip fraudPct={flag.fraudPct} certainty={flag.explanation.certainty} />
                                                    </StyledTableCell>
                                                    <StyledTableCell sx={{ maxWidth: 220, whiteSpace: "normal" }}>{partySplit(flag)}</StyledTableCell>
                                                    <StyledTableCell>{formatDateTime(flag.ride.completedAt)}</StyledTableCell>
                                                    <StyledTableCell>{bookingSourceLabel(flag.ride.bookingSource)}</StyledTableCell>
                                                    <StyledTableCell>{vehicleTypeLabel(flag.ride.type)}</StyledTableCell>
                                                    <StyledTableCell>{formatFare(flag.ride.totalFare)}</StyledTableCell>
                                                    <StyledTableCell sx={{ maxWidth: 420, whiteSpace: "normal" }}>
                                                        <FraudSignalChips signals={flag.signals} />
                                                    </StyledTableCell>
                                                    <StyledTableCell>{formatDateTime(flag.createdAt)}</StyledTableCell>
                                                    <StyledTableCell sx={{ maxWidth: 260, whiteSpace: "normal" }}>
                                                        {flag.status !== "OPEN" && (
                                                            <Typography variant="body2">
                                                                {fraudReviewerName(flag.reviewedBy)} · {formatDateTime(flag.reviewedAt)}
                                                            </Typography>
                                                        )}
                                                        {flag.responsibleParties.length > 0 && (
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                Responsible: {responsibleLabel(flag.responsibleParties)}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">{flag.note || "-"}</Typography>
                                                    </StyledTableCell>
                                                    {canReview && (
                                                        <StyledTableCell sx={{ maxWidth: "none" }}>
                                                            <FraudReviewActions rideId={flag.rideId} flag={flag} onChanged={flags.refetch} />
                                                        </StyledTableCell>
                                                    )}
                                                </StyledTableRow>
                                                {open && (
                                                    <StyledTableRow data-testid={`fraud-explain-${flag.rideId}`}>
                                                        <StyledTableCell colSpan={columns.length} sx={{ maxWidth: "none", whiteSpace: "normal" }}>
                                                            <Box sx={{ maxWidth: 900, py: 1 }}>
                                                                <FraudExplanation explanation={flag.explanation} />
                                                            </Box>
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {flags.rows.length === 0 && (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={columns.length} align="center">
                                                <Typography color="textSecondary" sx={{ py: 3 }}>
                                                    No {tabLabel} flags.
                                                </Typography>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    )}

                    <LoadMoreBar
                        count={flags.rows.length}
                        hasMore={flags.hasMore}
                        loading={flags.isLoadingMore}
                        onLoadMore={flags.loadMore}
                    />
                </>
            )}
        </StyledPaper>
    );
};

export default FraudReview;
